import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import { filterObjectByKeys } from '@/utils/qsHelper';
import Close from '@mui/icons-material/Close';
import {
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	ListItemText,
	MenuItem,
	Typography,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useCallback, useEffect, useState } from 'react';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useGetUser, useGetUserResultType } from '@/hooks/useGetUser';
import CloseIcon from '@mui/icons-material/Close';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import AddRelativeSummaryFilterSection from './AddRelativeSummaryFilterSection';
import { useAddRelativeFreeUserMutation } from '@/api/mockApiSlice';
import {
	useAddFriendsMutation,
	useGetFriendFreeListQuery,
} from '@/api/friend/friendApiSlice';
import {
	FriendFreeListFilter,
	FriendFreeListItem,
} from '@/api/DTO/friend/friend.interface';

const AddNonMemberRelativeListModal = () => {
	const { t } = useTranslation();
	const { userId, surveyId } = useParams();
	const [addFriends] = useAddFriendsMutation();

	const showModal = useModal();

	const [open, setOpen] = useState(false);

	const [addRelativeFreeUser] = useAddRelativeFreeUserMutation();

	const handleClose = () => {
		setOpen(false);
	};

	const { userRoles } = useGetUser<useGetUserResultType>({
		// shouldRefetch: true,
		roleObj: {
			read: ['read', 'user', 'user-list'],
			create: ['create', 'user'],
		},
	});

	const [selectedUser, setSelectedUser] = useState<string[]>([]);
	const [selectedUserItem, setSelectedUserItem] = useState<
		FriendFreeListItem[]
	>([]);

	const defaultFreeUserPageFilter: FriendFreeListFilter = {
		pagination: initialPagination,
		sortings: [],
		searchKey: '',
		userId: userId || '',
	};

	const defaultValues: DefaultValues<FriendFreeListFilter> =
		defaultFreeUserPageFilter;

	const [searchFilter, setSearchFilter] = useState<FriendFreeListFilter>(
		defaultFreeUserPageFilter
	);

	const methods = useForm<FriendFreeListFilter>({
		defaultValues,
	});

	const transformedSearchParams: FriendFreeListFilter = {
		...defaultFreeUserPageFilter,
		...filterObjectByKeys(searchFilter, [
			'pagination',
			'sortings',
			'searchKey',
			'userId',
		]),
	};

	const {
		data: freeUserPageSummary,
		isSuccess: isGetFreeUserPageSummarySuccess,
	} = useGetFriendFreeListQuery(transformedSearchParams, {
		// refetchOnMountOrArgChange: true,
		// skip: !userRoles.read,
	});

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as FriendFreeListFilter;
			setSearchFilter(propertyFilterFormResult);
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const transformResponse = useCallback((response?: FriendFreeListItem[]) => {
		if (response == null) return;
		const newResponse: FriendFreeListItem[] = [];
		response.forEach((item) => {
			newResponse.push({
				id: item.userId,
				userId: item.userId,
				email: item.email,
				lastLogin: item.lastLogin,
				username: item.username ?? '-',
				nickname: item.nickname ?? '-',
				phone: item.phone,
				district: item.district,
				disabled: item.disabled,
			});
		});
		return newResponse;
	}, []);

	const columns: GridColDef[] = [
		{
			field: 'username',
			headerName: t('common.username'),
			flex: 25,
			renderCell: (params) => (
				<Box>
					<Typography
						sx={{
							display: 'block',
						}}
						fontSize='body2'
						variant='regular'
					>
						{params.row.username}
					</Typography>
					<Typography
						sx={{
							display: 'block',
						}}
						fontSize='caption'
						color='common.neutral50'
						variant='regular'
					>
						{params.row.phone}
					</Typography>
					<Typography
						sx={{
							display: 'block',
						}}
						fontSize='caption'
						color='common.neutral50'
						variant='regular'
					>
						{params.row.email}
					</Typography>
				</Box>
			),
		},
		{
			field: 'district',
			headerName: t('common.belongedDistrict'),
			flex: 20,
		},
		// {
		// 	field: 'disabled',
		// 	headerName: t('common.status'),
		// 	flex: 10,
		// 	renderCell: (params) => (
		// 		<Chip
		// 			label={params.row.disabled ? t('common.freezed') : t('common.normal')}
		// 			color={params.row.disabled ? 'chipRed' : 'chipGreen'}
		// 			variant='outlined'
		// 		/>
		// 	),
		// },
		// {
		// 	field: 'lastLogin',
		// 	headerName: t('common.lastOnlineTime'),
		// 	flex: 20,
		// 	renderCell: (params) => (
		// 		<Typography variant='regular'>
		// 			{params.row.lastLogin
		// 				? dayjs(params.row.lastLogin)
		// 						.locale('zh-hk-my-setting')
		// 						.format(DISPLAY_CHINESE_DATE_TIME_FORMAT)
		// 				: '-'}
		// 		</Typography>
		// 	),
		// },
	];

	const handleAddSurveyFreeUser = async () => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={true}
				title={t('relative.addRelative')}
				titleProps={{ variant: 'h6' }}
				description={
					<>
						<Typography variant='body1'>
							{t('relative.addRelativeMsg')}
						</Typography>
						<ul>
							{selectedUserItem.map((user) => {
								return (
									<li key={user.userId}>
										<Typography variant='body1'>{user?.username}</Typography>
									</li>
								);
							})}
						</ul>
					</>
				}
				cancellationText={''}
				confirmationText={t('button.confirm')}
				confirmationButtonProps={{ variant: 'contained' }}
				onConfirm={async () => {
					const response = await addFriends({
						userId: userId || '',
						targetUserIds: selectedUser,
					});
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.addRelativeSuccessMsg'), {
							variant: 'success',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});
						handleClose();
					}
				}}
			/>
		));
	};

	return (
		<>
			<MenuItem
				onClick={() => {
					setOpen(true);
				}}
			>
				<ListItemText>{t('common.nonMemberUser')}</ListItemText>
			</MenuItem>

			<Dialog
				open={open}
				onClose={handleClose}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='lg'
				fullWidth
			>
				<DialogTitle variant='h5'>
					{t('relative.addRelative') + '：' + t('common.nonMemberUser')}
				</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={handleClose}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<Close />
				</IconButton>
				<DialogContent
					sx={{
						minHeight: '765px',
					}}
				>
					<FormProvider {...methods}>
						<AddRelativeSummaryFilterSection />

						<Box marginTop={2} minWidth={'800px'}>
							<AppDataGrid
								loading={!isGetFreeUserPageSummarySuccess}
								rows={transformResponse(freeUserPageSummary?.data?.items) ?? []}
								columns={columns}
								rowCount={freeUserPageSummary?.data?.pagination.totalItems ?? 0}
								getRowId={(row) => row.userId}
								checkboxSelection
								selectionModel={selectedUser}
								onSelectionModelChange={(ids) => {
									const rows =
										transformResponse(freeUserPageSummary?.data?.items) ?? [];
									const selectedIDs = new Set(ids);
									const selectedRowData = rows.filter((row) =>
										selectedIDs.has(row.userId.toString())
									);

									// Update selectedUser
									setSelectedUser(ids.map((id) => String(id)));

									// Update selectedUserItem
									setSelectedUserItem((prevSelected) => {
										const newSelected = new Set(ids.map((id) => String(id)));
										const updatedSelected = prevSelected.filter((item) =>
											newSelected.has(item.userId)
										);

										// Add newly selected items
										selectedRowData.forEach((row) => {
											if (
												!updatedSelected.some(
													(item) => item.userId === row.userId
												)
											) {
												updatedSelected.push(row);
											}
										});

										// Remove unselected items
										const allSelectedIDs = new Set([
											...prevSelected.map((item) => item.userId),
											...ids.map((id) => String(id)),
										]);
										return updatedSelected.filter((item) =>
											allSelectedIDs.has(item.userId)
										);
									});
								}}
							/>
						</Box>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button
						type='submit'
						variant='contained'
						color='primary'
						onClick={handleAddSurveyFreeUser}
						disabled={selectedUser.length === 0}
					>
						{t('button.adjunction')}
						{/* {selectedUser.length > 0 && `(${selectedUser.length})`} */}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default AddNonMemberRelativeListModal;
